import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import React, { useRef, useState, useMemo } from 'react';
import SquareIcon from '@mui/icons-material/Square';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import { OpcionesMenuFunc } from '../../../utils/componentes-utiles/OpcionesMenuFunc';
import { EditarUsuario } from './EditarUsuario';
import { delUsuario, getUsuarios, getTarjetaPorUsuario, eliminarTarjeta } from '../../../services/apis';
import { advertenciaEliminar } from '../../../utils/componentes-utiles/advertencias';
import { TarjetaDialog } from '../tarjetas/TarjetaDialog'; // ajusta ruta según donde lo ubiques

export const UsuarioCard = ({ usuario, opciones, cargarUsuarios, departamentos, roles, tipo = 'usuario' }) => {
    const [openEdit, setOpenEdit] = useState(false);

    // nuevo: estado del dialog de tarjeta
    const [tarjetaDialogOpen, setTarjetaDialogOpen] = useState(false);
    const [tarjetaSeleccionada, setTarjetaSeleccionada] = useState(null);
    const [tarjetaCargando, setTarjetaCargando] = useState(false);

    // Usar useMemo para optimizar las búsquedas
    const nombreRol = useMemo(() => {
        if (!roles || !usuario.rol) return 'Sin rol';
        const rolEncontrado = roles.find(r => r.id == usuario.rol);
        return rolEncontrado ? rolEncontrado.nombre : `Rol ${usuario.rol}`;
    }, [roles, usuario.rol]);

    const nombreDepartamento = useMemo(() => {
        if (!departamentos || !usuario.departamento) return 'Sin departamento';
        const deptoEncontrado = departamentos.find(d => d.id == usuario.departamento);
        return deptoEncontrado ? deptoEncontrado.nombre : `Depto ${usuario.departamento}`;
    }, [departamentos, usuario.departamento]);

    const getNombreCompleto = () => {
        if (usuario.nombre_completo) {
            return usuario.nombre_completo;
        }
        return `${usuario.nombre} ${usuario.apellidos || ''}`.trim();
    };

    const formatTelefono = (telefono) => {
        if (!telefono || telefono.trim() == '') return 'No especificado';
        const telefonoLimpio = telefono.replace(/\s+/g, '');
        if (telefonoLimpio.length <= 3) return telefonoLimpio;
        return `${telefonoLimpio.substring(0, 3)}•••`;
    };

    const formatNIF = (nif) => {
        if (!nif || nif.trim() == '') return 'No especificado';
        const nifLimpio = nif.trim().toUpperCase();
        if (nifLimpio.length <= 3) return nifLimpio;
        return `${nifLimpio.substring(0, 3)}•••`;
    };
    
    const getIcono = () => {
        if (tipo == 'dispositivo') {
            return (
                <DevicesIcon
                    sx={{
                        fontSize: 40,
                        color: usuario.color || '#666666'
                    }}
                />
            );
        }
        return (
            <AccountCircleIcon
                sx={{
                    fontSize: 40,
                    color: usuario.color || '#1976d2'
                }}
            />
        );
    };

    const getTituloEliminar = () => {
        if (tipo == 'dispositivo') {
            return "Eliminar " + usuario.nombre;
        }
        return "Eliminar " + usuario.nombre;
    };

    const getTextoEliminar = () => {
        if (tipo == 'dispositivo') {
            return "Vas a eliminar permanentemente este dispositivo";
        }
        return "Vas a eliminar permanentemente este usuario";
    };

    const handleEvent = (opcionId) => {
        opcionId = opcionId.toString(); // Asegurarse de que sea string para el switch
        switch (opcionId) {
            case "1":
                setOpenEdit(true);
                break;
            case "2":
                eliminarUsuario(usuario.id);
                break;
            case "3":
                // NUEVO: ver tarjeta
                abrirDialogTarjeta(usuario.id);
                break;
            default:
                console.log("Lógica para una opción desconocida");
                break;
        }
    };

    const eliminarUsuario = async (id) => {
        const titulo = getTituloEliminar();
        const texto = getTextoEliminar();

        const confirmado = await advertenciaEliminar(titulo, texto);
        if (confirmado) {
            try {
                const response = await delUsuario(id);
                if (response.success == true) {
                    cargarUsuarios();
                }
            } catch (error) {
                console.error(`Error al eliminar el ${tipo}:`, error);
            }
        }
    };

    // NUEVO: abrir dialog de tarjeta y traerla del backend
    const abrirDialogTarjeta = async (idUsuario) => {
        setTarjetaCargando(true);
        try {
            const resp = await getTarjetaPorUsuario({ id_usuario: idUsuario });
            // resp debería ser { success: true, tarjeta: {...} } o success:false
            if (resp && resp.success && resp.tarjeta) {
                setTarjetaSeleccionada(resp.tarjeta);
                setTarjetaDialogOpen(true);
            } else {
                // Si el backend devuelve estructura distinta (por ejemplo data.tarjeta) adapta aquí
                // Mostrar feedback al admin
                console.warn('No se encontró tarjeta para el usuario', resp);
                alert(resp?.message || 'Este usuario no tiene tarjeta creada.');
            }
        } catch (err) {
            console.error('Error al obtener tarjeta del usuario:', err);
            alert('Error al obtener la tarjeta. Revisa la consola.');
        } finally {
            setTarjetaCargando(false);
        }
    };

    return (
        <>
        <Grid
            container
            xs={12}
            gap={1}
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="fichaje-card fade-in2"
            sx={{ padding: 2, borderRadius: 3, boxShadow: 3, background: "var(--fondoTarjeta)" }}
        >
            {/* Header con avatar/icono y nombre */}
            <Grid
                container
                direction="row"
                justifyContent="start"
                alignItems="center"
                xs={12}
                gap={1.5}
            >
                <Grid item xs={2}>
                    {getIcono()}
                </Grid>

                <Grid item xs={9}>
                    <Typography
                        variant='h6'
                        className='ellip'
                        sx={{ textAlign: 'left' }}
                    >
                        {getNombreCompleto()}
                    </Typography>
                </Grid>
            </Grid>

            {/* Información del usuario/dispositivo */}
            <Grid
                container
                direction="column"
                justifyContent="start"
                alignItems="start"
                xs={12}
                gap={0.5}
            >
                <Typography variant='caption' sx={{ lineHeight: 1.2 }}>
                    <strong>Rol:</strong> {nombreRol}
                </Typography>

                {tipo == 'usuario' && (
                    <>
                        <Typography variant='caption' sx={{ lineHeight: 1.2 }}>
                            <strong>Departamento:</strong> {nombreDepartamento}
                        </Typography>

                        <Typography variant='caption' sx={{ lineHeight: 1.2 }}>
                            <strong>Email:</strong> {usuario.mail || 'No especificado'}
                        </Typography>

                        {usuario.telefono && (
                            <Typography variant='caption' sx={{ lineHeight: 1.2 }}>
                                <strong>Teléfono:</strong> {formatTelefono(usuario.telefono)}
                            </Typography>
                        )}

                        {usuario.nif && (
                            <Typography variant='caption' sx={{ lineHeight: 1.2 }}>
                                <strong>NIF:</strong> {formatNIF(usuario.nif)}
                            </Typography>
                        )}
                    </>
                )}

            </Grid>

            {/* Opciones del menú */}
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                xs={12}
                sx={{ mt: 1 }}
            >
                {opciones && opciones.length > 0 ? (
                    <OpcionesMenuFunc
                        small={true}
                        variant={"text"}
                        justifyContent={'space-between'}
                        opciones={opciones}
                        handleEvent={handleEvent}
                    />
                ) : (
                    <Typography variant="caption" color="textSecondary">
                        No hay opciones disponibles
                    </Typography>
                )}
            </Grid>

            {/* Dialog de edición */}
            {openEdit && (
                <EditarUsuario
                    open={openEdit}
                    setOpen={setOpenEdit}
                    cargarUsuarios={cargarUsuarios}
                    departamentos={departamentos}
                    roles={roles}
                    usuarioData={usuario}
                />
            )}
        </Grid>

        {/* DIALOG: mostrar tarjeta y permitir descarga */}
        <TarjetaDialog
            open={tarjetaDialogOpen}
            onClose={() => { setTarjetaDialogOpen(false); setTarjetaSeleccionada(null); }}
            tarjeta={tarjetaSeleccionada}
        />
        </>
    );
};
